import React from "react";
import { Globe, Cpu, TrendingUp, Scale, Rocket, ExternalLink, GitCompareArrows, Sparkles, Layers, AlertTriangle, Route, ListChecks, ShieldAlert, Gauge } from "lucide-react";

function renderInline(text, keyPrefix) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={`${keyPrefix}-b-${i}`}>{p.slice(2, -2)}</strong>;
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{p}</React.Fragment>;
  });
}

const Paragraphs = ({ text, className }) => {
  const paras = String(text || "").split(/\n{2,}|\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className={className}>
      {paras.map((p, i) => (
        <p key={`${i}-${p.slice(0, 20)}`} className="mb-3 last:mb-0">{renderInline(p, `pp${i}`)}</p>
      ))}
    </div>
  );
};

const ACCENTS = {
  rexxpons: { border: "border-primary/30", bg: "bg-primary/[0.04]", chipBorder: "border-primary/40", chipBg: "bg-primary/10", text: "text-primary", sub: "text-primary/80" },
  rexxengine: { border: "border-violet-500/30", bg: "bg-violet-500/[0.05]", chipBorder: "border-violet-500/40", chipBg: "bg-violet-500/10", text: "text-violet-300", sub: "text-violet-300/80" },
  rating: { border: "border-amber-500/30", bg: "bg-amber-500/[0.05]", chipBorder: "border-amber-500/40", chipBg: "bg-amber-500/10", text: "text-amber-300", sub: "text-amber-300/80" },
  legal: { border: "border-emerald-500/30", bg: "bg-emerald-500/[0.05]", chipBorder: "border-emerald-500/40", chipBg: "bg-emerald-500/10", text: "text-emerald-300", sub: "text-emerald-300/80" },
  growth: { border: "border-fuchsia-500/30", bg: "bg-fuchsia-500/[0.05]", chipBorder: "border-fuchsia-500/40", chipBg: "bg-fuchsia-500/10", text: "text-fuchsia-300", sub: "text-fuchsia-300/80" },
};

const EngineCard = ({ accent, icon: Icon, name, role, note, testid, children }) => (
  <div data-testid={testid} className={`rounded-2xl border ${accent.border} ${accent.bg} p-6`}>
    <div className="flex items-center gap-2.5 mb-1.5">
      <span className={`flex items-center justify-center w-8 h-8 rounded-lg border ${accent.chipBorder} ${accent.chipBg} ${accent.text} shrink-0`}>
        <Icon strokeWidth={1.7} className="w-4 h-4" />
      </span>
      <div className="leading-tight">
        <p className="font-sans text-sm font-semibold text-foreground">{name}</p>
        <p className={`font-sans text-[10px] tracking-[0.16em] uppercase ${accent.sub}`}>{role}</p>
      </div>
    </div>
    {note && <p className="font-sans text-xs text-muted-foreground mb-4">{note}</p>}
    {children}
  </div>
);

const SubLabel = ({ accent, icon: Icon, children }) => (
  <p className={`font-sans text-[10px] tracking-[0.18em] uppercase ${accent} mb-2.5 flex items-center gap-1.5`}>
    {Icon && <Icon strokeWidth={1.6} className="w-3.5 h-3.5" />} {children}
  </p>
);

const FusionBanner = ({ fusion }) => {
  if (!fusion) return null;
  return (
    <div data-testid="fusion-layer" className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/[0.10] to-violet-500/[0.06] p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-sans text-xs tracking-[0.18em] uppercase text-primary flex items-center gap-2">
          <Sparkles strokeWidth={1.7} className="w-4 h-4" /> Cognitive Fusion
        </p>
        {fusion.insight_level && (
          <span data-testid="insight-level" className="font-sans text-[10px] tracking-[0.16em] uppercase text-primary-foreground bg-primary/90 rounded-full px-3 py-1">
            {fusion.insight_level} insight
          </span>
        )}
      </div>
      {fusion.summary && <p className="font-serif text-lg sm:text-xl font-light leading-snug text-foreground mb-4">{fusion.summary}</p>}
      {fusion.combined_interpretation && (
        <Paragraphs text={fusion.combined_interpretation} className="font-sans text-sm leading-relaxed text-foreground/85" />
      )}
    </div>
  );
};

const SYSTEM_CHIPS = {
  Rexxpons: { icon: Globe, cls: "border-primary/40 bg-primary/10 text-primary" },
  Rexxengine: { icon: Cpu, cls: "border-violet-500/40 bg-violet-500/10 text-violet-300" },
  RatingOnline: { icon: TrendingUp, cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  RexxLegal: { icon: Scale, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  "Growth-Compass": { icon: Rocket, cls: "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300" },
};

const SystemsActivated = ({ systems, note }) => {
  const chips = ["Rexxpons", ...(systems || [])];
  return (
    <div data-testid="systems-activated" className="rounded-2xl border border-border bg-secondary/30 p-5 mb-6">
      <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Systems Activated</p>
      <div className="flex flex-wrap gap-2">
        {chips.map((name) => {
          const c = SYSTEM_CHIPS[name] || SYSTEM_CHIPS.Rexxpons;
          const Icon = c.icon;
          return (
            <span key={name} data-testid={`system-chip-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-sans text-xs font-medium ${c.cls}`}>
              <Icon strokeWidth={1.8} className="w-3.5 h-3.5" /> {name}
            </span>
          );
        })}
      </div>
      {note && <p data-testid="activation-note" className="font-sans text-sm text-muted-foreground leading-relaxed mt-4">{note}</p>}
    </div>
  );
};

const IndexBadge = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-border bg-background/50 px-4 py-3">
    <p className="font-sans text-[10px] tracking-[0.16em] uppercase text-muted-foreground mb-1.5 flex items-center gap-1.5">
      <Icon strokeWidth={1.7} className="w-3.5 h-3.5" /> {label}
    </p>
    <p className="font-sans text-sm font-semibold text-foreground leading-snug">{value || "—"}</p>
  </div>
);

const DecisionLayer = ({ decision }) => {
  if (!decision) return null;
  return (
    <div data-testid="decision-layer" className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.05] p-6 mb-6">
      <p className="font-sans text-xs tracking-[0.18em] uppercase text-rose-300 flex items-center gap-2 mb-4">
        <Route strokeWidth={1.7} className="w-4 h-4" /> Decision Layer
      </p>
      {decision.recommended_path && (
        <div className="mb-5">
          <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-rose-300/80 mb-2">Recommended Path</p>
          <p data-testid="recommended-path" className="font-serif text-lg font-light leading-snug text-foreground">{decision.recommended_path}</p>
        </div>
      )}
      {(decision.priority_actions || []).length > 0 && (
        <div className="mb-5">
          <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-rose-300/80 mb-2.5 flex items-center gap-1.5">
            <ListChecks strokeWidth={1.6} className="w-3.5 h-3.5" /> Priority Actions
          </p>
          <ol className="space-y-2">
            {decision.priority_actions.map((a, i) => (
              <li key={`${i}-${a.slice(0, 16)}`} data-testid="priority-action-item" className="flex items-start gap-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-rose-500/20 text-rose-300 font-sans text-[10px] font-semibold shrink-0 mt-0.5">{i + 1}</span>
                <span className="font-sans text-sm text-foreground/90 leading-relaxed">{a}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <IndexBadge icon={ShieldAlert} label="Risk Level" value={decision.risk_level} />
        <IndexBadge icon={Scale} label="Legal Urgency" value={decision.legal_urgency_index} />
        <IndexBadge icon={Gauge} label="Financial Sustainability" value={decision.financial_sustainability_index} />
      </div>
    </div>
  );
};

// Unified Cognitive Panel: fuses Rexxpons (browser), Rexxengine (reasoning),
// RatingOnline (financial) and RexxLegal (legal) — four independent systems,
// clearly attributed, plus a cross-engine fusion layer.
export const UnifiedCognitivePanel = ({ unified }) => {
  if (!unified) return null;
  const results = (unified.rexxpons_search || {}).results || [];
  const engine = unified.rexxengine_reasoning || null;
  const rating = unified.ratingonline_evaluation || null;
  const legal = unified.rexxlegal_analysis || null;
  const growth = unified.growth_compass_analysis || null;
  const fusion = unified.fusion_layer || null;

  const knowledge = (engine || {}).structured_knowledge || [];
  const comparisons = (engine || {}).comparisons || [];
  const engMs = engine?.metadata?.processing_time_ms;

  return (
    <section id="unified" data-testid="unified-cognitive-panel" className="scroll-mt-28 py-20">
      <div className="flex items-baseline gap-4 mb-3">
        <span className="font-sans text-xs text-primary tracking-[0.2em]">✦</span>
        <h2 className="font-serif font-medium text-2xl sm:text-3xl tracking-tight">Unified Cognitive Panel</h2>
      </div>
      <p className="font-sans text-xs tracking-[0.14em] uppercase text-muted-foreground mb-8 flex items-center gap-2">
        <Layers strokeWidth={1.5} className="w-3.5 h-3.5" /> Rexxpons · Rexxengine{rating ? " · RatingOnline" : ""}{legal ? " · RexxLegal" : ""}{growth ? " · Growth-Compass" : ""} — independent systems, one understanding
      </p>

      <SystemsActivated systems={unified.systems_activated} note={unified.activation_note} />
      <FusionBanner fusion={fusion} />
      <DecisionLayer decision={unified.decision_layer} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Rexxpons */}
        <EngineCard accent={ACCENTS.rexxpons} icon={Globe} name="Rexxpons" role="Cognitive Web Browser" note="Sources gathered & synthesized by Rexxpons." testid="rexxpons-search-block">
          <ul className="space-y-2.5">
            {results.length === 0 && <li className="font-sans text-sm text-muted-foreground">No search results.</li>}
            {results.map((r, i) => (
              <li key={`${i}-${r.url}`} data-testid="rexxpons-result-item">
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="group flex items-start justify-between gap-3 rounded-lg border border-border hover:border-primary/40 bg-background/40 hover:bg-secondary/40 px-3 py-2.5 transition-colors">
                  <div className="min-w-0">
                    <p className="font-sans text-sm text-foreground truncate">{r.title}</p>
                    <p className="font-sans text-xs text-muted-foreground truncate">{r.snippet || r.url}</p>
                  </div>
                  <ExternalLink strokeWidth={1.5} className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </EngineCard>

        {/* Rexxengine */}
        {engine && (
          <EngineCard accent={ACCENTS.rexxengine} icon={Cpu} name="Rexxengine" role="Cognitive Reasoning Engine" note="Reasoned independently from the Cognitive Command." testid="rexxengine-reasoning-block">
            {engine.explanation && <Paragraphs text={engine.explanation} className="font-sans text-sm leading-relaxed text-foreground/90 mb-5" />}
            {knowledge.length > 0 && (
              <div className="mb-5">
                <SubLabel accent="text-violet-300/80">Structured Knowledge</SubLabel>
                <div className="space-y-2.5">
                  {knowledge.map((k, i) => (
                    <div key={`${i}-${k.concept}`} data-testid="structured-knowledge-item" className="border-l-2 border-violet-500/40 pl-3">
                      <p className="font-sans text-sm font-medium text-foreground">{k.concept}</p>
                      <p className="font-sans text-xs text-muted-foreground leading-relaxed">{k.details}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {comparisons.length > 0 && (
              <div className="mb-5">
                <SubLabel accent="text-violet-300/80" icon={GitCompareArrows}>Comparisons</SubLabel>
                <div className="space-y-3">
                  {comparisons.map((c, i) => (
                    <div key={`${i}-${c.item_a}`} data-testid="comparison-item" className="rounded-lg border border-border bg-background/40 px-3 py-2.5">
                      <p className="font-sans text-xs font-medium text-foreground mb-1">
                        <span className="text-violet-300">{c.item_a}</span> <span className="text-muted-foreground">vs</span> <span className="text-primary">{c.item_b}</span>
                      </p>
                      <p className="font-sans text-xs text-muted-foreground leading-relaxed">{c.analysis}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {engine.deep_reasoning && (
              <div data-testid="deep-reasoning-block" className="rounded-xl border border-violet-500/40 bg-violet-500/[0.08] p-4 mb-4">
                <SubLabel accent="text-violet-300" icon={Sparkles}>Deep Reasoning · Pro</SubLabel>
                <Paragraphs text={engine.deep_reasoning} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
            {typeof engMs === "number" && <p className="font-sans text-[10px] tracking-[0.14em] uppercase text-muted-foreground/70">Rexxengine · processed in {engMs} ms</p>}
          </EngineCard>
        )}

        {/* RatingOnline */}
        {rating && (
          <EngineCard accent={ACCENTS.rating} icon={TrendingUp} name="RatingOnline" role="Financial Rating Engine" note="Indicative financial & rating evaluation." testid="ratingonline-block">
            {rating.score && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.08] px-4 py-3 mb-4">
                <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-amber-300/80 mb-1">Indicative Score</p>
                <p data-testid="rating-score" className="font-serif text-lg text-foreground leading-snug">{rating.score}</p>
              </div>
            )}
            {(rating.financial_risks || []).length > 0 && (
              <div className="mb-5">
                <SubLabel accent="text-amber-300/80" icon={AlertTriangle}>Financial Risks</SubLabel>
                <div className="space-y-2.5">
                  {rating.financial_risks.map((r, i) => (
                    <div key={`${i}-${r.risk}`} data-testid="financial-risk-item" className="border-l-2 border-amber-500/40 pl-3">
                      <p className="font-sans text-sm font-medium text-foreground">{r.risk}</p>
                      <p className="font-sans text-xs text-muted-foreground leading-relaxed">{r.impact}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rating.balance_sheet_analysis && (
              <div>
                <SubLabel accent="text-amber-300/80">Balance-Sheet Analysis</SubLabel>
                <Paragraphs text={rating.balance_sheet_analysis} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
          </EngineCard>
        )}

        {/* RexxLegal */}
        {legal && (
          <EngineCard accent={ACCENTS.legal} icon={Scale} name="RexxLegal" role="Legal & Compliance Engine" note="Informational — not formal legal advice." testid="rexxlegal-block">
            {(legal.legal_risks || []).length > 0 && (
              <div className="mb-5">
                <SubLabel accent="text-emerald-300/80" icon={AlertTriangle}>Legal Risks</SubLabel>
                <div className="space-y-2.5">
                  {legal.legal_risks.map((r, i) => (
                    <div key={`${i}-${r.issue}`} data-testid="legal-risk-item" className="border-l-2 border-emerald-500/40 pl-3">
                      <p className="font-sans text-sm font-medium text-foreground">{r.issue}</p>
                      <p className="font-sans text-xs text-muted-foreground leading-relaxed">{r.analysis}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {legal.compliance_assessment && (
              <div className="mb-5">
                <SubLabel accent="text-emerald-300/80">Compliance Assessment</SubLabel>
                <Paragraphs text={legal.compliance_assessment} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
            {legal.strategic_legal_advice && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.08] p-4">
                <SubLabel accent="text-emerald-300" icon={Sparkles}>Strategic Legal Advice</SubLabel>
                <Paragraphs text={legal.strategic_legal_advice} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
          </EngineCard>
        )}

        {/* Growth-Compass */}
        {growth && (
          <EngineCard accent={ACCENTS.growth} icon={Rocket} name="Growth-Compass" role="Strategic Growth Engine" note="Indicative business plan, projections & growth strategy." testid="growthcompass-block">
            {(growth.investment_analysis || []).length > 0 && (
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                {growth.investment_analysis.map((m, i) => (
                  <div key={`${i}-${m.metric}`} data-testid="investment-metric-item" className="rounded-xl border border-fuchsia-500/40 bg-fuchsia-500/[0.08] px-3 py-2.5">
                    <p className="font-sans text-[10px] tracking-[0.14em] uppercase text-fuchsia-300/80">{m.metric}</p>
                    <p className="font-sans text-sm font-semibold text-foreground leading-snug">{m.value}</p>
                  </div>
                ))}
              </div>
            )}
            {growth.growth_strategy && (
              <div className="mb-5">
                <SubLabel accent="text-fuchsia-300/80" icon={Route}>Growth Strategy</SubLabel>
                <Paragraphs text={growth.growth_strategy} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
            {growth.projections && (
              <div className="mb-5">
                <SubLabel accent="text-fuchsia-300/80">Projections</SubLabel>
                <Paragraphs text={growth.projections} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
            {growth.industrial_plan && (
              <div>
                <SubLabel accent="text-fuchsia-300/80">Industrial Plan</SubLabel>
                <Paragraphs text={growth.industrial_plan} className="font-sans text-sm leading-relaxed text-foreground/90" />
              </div>
            )}
          </EngineCard>
        )}
      </div>
    </section>
  );
};
